/* eslint-disable @typescript-eslint/explicit-function-return-type */
import type {Routes} from "@angular/router";
import {PermissionId} from "../../models/users/Permission";
import {UserAccountType} from "../../models/users/User";
import {hasPermissions, hasUserTypes, isLoggedIn} from "../../services/guards/permission-guard";

export const pricingRoutes = [
  // Articles
  {
    path: "articles",
    canActivateChild: [isLoggedIn, hasUserTypes(UserAccountType.Admin)],
    children: [
      {
        path: "",
        loadComponent: () => import("./articles/list/articles-list").then(m => m.ArticlesList),
      },
      {
        path: ":articleNumber",
        children: [
          {
            path: "translations",
            canActivate: [hasPermissions(PermissionId.EditTranslations)],
            loadComponent: () => import("./articles/translations/article-translations").then(m => m.ArticleTranslations),
          },
        ],
      },
    ],
  },

  // Budget Numbers
  {
    path: "budget-numbers",
    canActivateChild: [isLoggedIn, hasPermissions(PermissionId.ManageBudgetNumbers)],
    children: [
      {
        path: "",
        loadComponent: () => import("./budget-numbers/list/budget-numbers-list").then(m => m.BudgetNumbersList),
      },
      {
        path: "bulk-import",
        canActivate: [hasUserTypes(UserAccountType.Admin)],
        loadComponent: () => import("./bulk-update/budget-numbers/bulk-import-budget-numbers").then(m => m.BulkImportBudgetNumbers),
      },
      {
        path: "create",
        loadComponent: () => import("./budget-numbers/form/budget-number-form").then(m => m.BudgetNumberForm),
      },
      {
        path: ":budgetNumberId",
        loadComponent: () => import("./budget-numbers/form/budget-number-form").then(m => m.BudgetNumberForm),
      },
    ],
  },

  // Currencies
  {
    path: "currencies",
    canActivateChild: [isLoggedIn, hasPermissions(PermissionId.ManageCurrencies)],
    children: [
      {
        path: "",
        loadComponent: () => import("./currencies/list/currencies-list").then(m => m.CurrenciesList),
      },
      {
        path: "create",
        loadComponent: () => import("./currencies/form/currency-form").then(m => m.CurrencyForm),
      },
      {
        path: ":currencyIsoCode",
        children: [
          // Exchange Rates
          {
            path: "exchange-rates",
            children: [
              {
                path: "",
                loadComponent: () => import("./currencies/exchange-rates/list/exchange-rates-list").then(m => m.ExchangeRatesList),
              },
              {
                path: "create",
                loadComponent: () => import("./currencies/exchange-rates/form/exchange-rate-form").then(m => m.ExchangeRateForm),
              },
              {
                path: ":exchangeRateId",
                loadComponent: () => import("./currencies/exchange-rates/form/exchange-rate-form").then(m => m.ExchangeRateForm),
              },
            ],
          },
        ],
      },
    ],
  },

  // Price Lists
  {
    path: "price-lists",
    canActivateChild: [isLoggedIn],
    children: [
      {
        path: "",
        loadComponent: () => import("./price-lists/list/price-lists-list").then(m => m.PriceListsList),
      },
      {
        path: "create",
        canActivate: [hasPermissions(PermissionId.EditPriceList)],
        loadComponent: () => import("./price-lists/form/price-lists-form").then(m => m.PriceListsForm),
      },
      {
        path: "bulk-update",
        canActivateChild: [hasUserTypes(UserAccountType.Admin)],
        children: [
          {
            path: "prices",
            loadComponent: () => import("./bulk-update/price-lists/bulk-update-price-lists").then(m => m.BulkUpdatePriceLists),
          },
          {
            path: "special-prices",
            loadComponent: () => import("./bulk-update/special-prices/bulk-update-special-prices").then(m => m.BulkUpdateSpecialPrices),
          },
        ],
      },
      {
        path: ":priceListId",
        children: [
          {
            path: "",
            canActivate: [hasPermissions(PermissionId.EditPriceList)],
            loadComponent: () => import("./price-lists/form/price-lists-form").then(m => m.PriceListsForm),
          },
          {
            path: "prices",
            loadComponent: () => import("./price-lists/prices/price-list-prices").then(m => m.PriceListPrices),
          },
        ],
      },
    ],
  },
] as const satisfies Routes;
